import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import U2000SaveCancelDialog from "../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000CheckBoxBase from "../../../u2000Core/components/formControls/checkbox/U2000CheckBoxBase";
import { useU2000AuthContext } from "../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000LogicalUnitContext } from "../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import { NS, U3125ROLES } from "../../U3125";
import { useU3125Context } from "../../U3125Context";
import {
  u3125BuildForfaitWizardCommitBody,
  u3125ForfaitWizardPreviewLineIsOk,
  u3125ForfaitWizardTodayIsoDate,
  u3125MapPreviewToEditableLines,
  type U3125ForfaitWizardCommitLineResult,
  type U3125ForfaitWizardEditableLine,
  type U3125ForfaitWizardPreviewLine,
  type U3125ForfaitWizardProjectTag,
  type U3125InvoiceTypeInitItem,
} from "../../U3125Service";
import { useU3125ForfaitWizardHandlers } from "../../U3125UseForfaitWizardHandlers";
import { useU3125OpenInvoicePdfHandlers } from "../../U3125UseApiHandlers";
import U3125ForfaitWizardCommitResults from "./U3125ForfaitWizardCommitResults";
import U3125ForfaitWizardInvoiceDateInput from "./U3125ForfaitWizardInvoiceDateInput";
import U3125ForfaitWizardPreviewGrid from "./U3125ForfaitWizardPreviewGrid";
import U3125ProjectNumberTagInput from "./U3125ProjectNumberTagInput";

type WizardStep = 1 | 2 | "results";

const U3125ForfaitWizardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const { luStateHandlers } = useU3125Context();
  const { preview, commit } = useU3125ForfaitWizardHandlers();
  const { openPdfInNewTab } = useU3125OpenInvoicePdfHandlers({
    onPopupBlocked: () => toast.error(t("msgPdfNewTabBlocked", { ns: NS })),
  });

  const canRelease = hasRole(U3125ROLES.EDIT);
  const canViewTecLines = hasRole(U3125ROLES.VIEWTECLINES);

  const [step, setStep] = useState<WizardStep>(1);
  const [tags, setTags] = useState<U3125ForfaitWizardProjectTag[]>([]);
  const [previewLines, setPreviewLines] = useState<U3125ForfaitWizardPreviewLine[]>([]);
  const [editableLines, setEditableLines] = useState<U3125ForfaitWizardEditableLine[]>([]);
  const [invoiceDate, setInvoiceDate] = useState(() => u3125ForfaitWizardTodayIsoDate());
  const [releaseAfterCreate, setReleaseAfterCreate] = useState(canRelease);
  const [commitResults, setCommitResults] = useState<U3125ForfaitWizardCommitLineResult[] | null>(null);
  const [loading, setLoading] = useState<"preview" | "commit" | null>(null);
  const [confirmReleaseOpen, setConfirmReleaseOpen] = useState(false);

  const invoiceTypes = useMemo(() => {
    const p = luStateHandlers.getPartialPersonnalization(["invoiceTypes"]) as { invoiceTypes?: U3125InvoiceTypeInitItem[] };
    return p?.invoiceTypes ?? [];
  }, [luStateHandlers]);

  const showPdfAction = useMemo(() => {
    const p = luStateHandlers.getPartialPersonnalization(["enableInvoicePdfAndLines"]) as { enableInvoicePdfAndLines?: boolean };
    return Boolean(p?.enableInvoicePdfAndLines);
  }, [luStateHandlers]);

  const allowInvoiceAboveBudget = useMemo(() => {
    const p = luStateHandlers.getPartialPersonnalization(["allowInvoiceAboveBudget"]) as { allowInvoiceAboveBudget?: boolean };
    return Boolean(p?.allowInvoiceAboveBudget);
  }, [luStateHandlers]);

  useEffect(() => {
    setLogicalUnitBreadcrumb([
      { label: t("title", { ns: NS }), href: "/U3125" },
      { label: t("wizardForfaitBreadcrumb", { ns: NS }) },
    ]);
    return () => setLogicalUnitBreadcrumb([]);
  }, [setLogicalUnitBreadcrumb, t]);

  const resetWizard = useCallback(() => {
    setStep(1);
    setTags([]);
    setPreviewLines([]);
    setEditableLines([]);
    setInvoiceDate(u3125ForfaitWizardTodayIsoDate());
    setReleaseAfterCreate(canRelease);
    setCommitResults(null);
    setLoading(null);
    setConfirmReleaseOpen(false);
  }, [canRelease]);

  const handleBackToList = useCallback(() => {
    navigate("/U3125");
  }, [navigate]);

  const handleContinue = useCallback(async () => {
    if (tags.length === 0) return;
    setLoading("preview");
    const lines = await preview(tags.map((tag) => tag.projetNumber));
    setLoading(null);
    if (!lines) return;
    setPreviewLines(lines);
    setEditableLines(u3125MapPreviewToEditableLines(lines));
    setStep(2);
  }, [tags, preview]);

  const validateBeforeCommit = useCallback((): boolean => {
    const included = editableLines.filter((l) => l.included);
    if (included.length === 0) {
      toast.error(t("errWizardNoProjectSelected", { ns: NS }));
      return false;
    }
    for (const line of included) {
      const preview = previewLines.find((p) => u3125ForfaitWizardPreviewLineIsOk(p) && p.projectId === line.projectId);
      const remaining = (preview && preview.status === 'ok' ? preview.remainingBudget : undefined) ?? 0;
      const amount = Number(line.invoiceAmount) || 0;
      if (amount <= 0) {
        toast.error(t("errWizardAmountMustBePositive", { ns: NS }));
        return false;
      }
      if (!allowInvoiceAboveBudget && amount > remaining + 0.01) {
        toast.error(t("errWizardAmountExceedsRemaining", { ns: NS, max: remaining.toFixed(2) }));
        return false;
      }
    }
    if (!invoiceDate?.trim()) {
      toast.error(t("errInvoiceDateRequired", { ns: NS }));
      return false;
    }
    if (releaseAfterCreate && !canRelease) {
      toast.error(t("errWizardReleaseRequiresEdit", { ns: NS }));
      return false;
    }
    return true;
  }, [editableLines, previewLines, invoiceDate, releaseAfterCreate, canRelease, allowInvoiceAboveBudget, toast, t]);

  const runCommit = useCallback(async () => {
    if (!validateBeforeCommit()) return;
    setLoading("commit");
    const body = u3125BuildForfaitWizardCommitBody({
      invoiceDate,
      releaseAfterCreate,
      previewLines,
      editableLines,
    });
    const results = await commit(body);
    setLoading(null);
    if (!results) return;

    const created = results.filter((r) => r.status === "created").length;
    const errors = results.filter((r) => r.status === "error").length;
    if (errors === 0) {
      toast.success(t("msgWizardCommitSuccess", { ns: NS, count: created }));
    } else {
      toast.warning(t("msgWizardCommitPartial", { ns: NS }));
    }
    setCommitResults(results);
    setStep("results");
  }, [validateBeforeCommit, invoiceDate, releaseAfterCreate, previewLines, editableLines, commit, toast, t]);

  const handleCommitClick = useCallback(() => {
    if (releaseAfterCreate) {
      setConfirmReleaseOpen(true);
      return;
    }
    void runCommit();
  }, [releaseAfterCreate, runCommit]);

  const commitLabel = releaseAfterCreate ? t("wizardCreateAndRelease", { ns: NS }) : t("wizardCreateInvoices", { ns: NS });

  return (
    <div className="flex w-full min-w-0 flex-1 flex-col gap-6 px-4 py-2 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 pb-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{t("wizardForfaitTitle", { ns: NS })}</h1>
          <nav className="mt-3 flex items-center gap-4 text-sm" aria-label={t("wizardStepsAria", { ns: NS })}>
            <span className={step === 1 ? "font-semibold text-blue-700" : step === "results" ? "text-gray-500" : "text-gray-600"}>
              ● {t("wizardStep1Title", { ns: NS })}
            </span>
            <span className="text-gray-300">────</span>
            <span className={step === 2 ? "font-semibold text-blue-700" : step === "results" ? "text-gray-500" : "text-gray-400"}>
              ○ {t("wizardStep2Title", { ns: NS })}
            </span>
          </nav>
        </div>
        <U2000ActionBtn label={t("wizardBackToList", { ns: NS })} handleClick={handleBackToList} />
      </div>

      {step === 1 && (
        <section className="flex w-full min-w-0 flex-col gap-4">
          <div>
            <h2 className="text-lg font-medium text-gray-900">{t("wizardStep1Title", { ns: NS })}</h2>
            <p className="mt-1 text-sm text-gray-600">{t("wizardProjectNumbersHelp", { ns: NS })}</p>
          </div>
          <label className="block text-sm font-medium text-gray-700">{t("wizardProjectNumbersLabel", { ns: NS })}</label>
          <U3125ProjectNumberTagInput
            value={tags}
            onChange={setTags}
            disabled={loading === "preview"}
            placeholder={t("wizardTagPlaceholder", { ns: NS })}
          />
          <div className="flex justify-end">
            <U2000ActionBtn
              label={t("wizardContinue", { ns: NS })}
              handleClick={() => void handleContinue()}
              disabled={tags.length === 0 || loading === "preview"}
            />
          </div>
        </section>
      )}

      {step === 2 && (
        <section className="flex w-full min-w-0 flex-col gap-4">
          <button type="button" className="self-start text-sm text-blue-600 hover:underline" onClick={() => setStep(1)}>
            ← {t("wizardModifySelection", { ns: NS })}
          </button>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <U3125ForfaitWizardInvoiceDateInput
              value={invoiceDate}
              onChange={setInvoiceDate}
              label={t("lblInvoiceDate", { ns: NS })}
              disabled={loading === "commit"}
              inlineEnd={
                canRelease ? (
                  <div title={t("wizardReleaseTooltip", { ns: NS })}>
                    <U2000CheckBoxBase
                      fieldName="wizardReleaseAfterCreate"
                      label={t("wizardReleaseAfterCreate", { ns: NS })}
                      value={releaseAfterCreate}
                      setValue={setReleaseAfterCreate}
                      readonly={loading === "commit"}
                    />
                  </div>
                ) : undefined
              }
            />
            {!canRelease && <p className="mt-2 text-sm text-gray-500">{t("errWizardReleaseRequiresEdit", { ns: NS })}</p>}
          </div>

          <U3125ForfaitWizardPreviewGrid
            previewLines={previewLines}
            editableLines={editableLines}
            invoiceTypes={invoiceTypes}
            i18nLanguage={i18n.language}
            onEditableLinesChange={setEditableLines}
            onCommit={handleCommitClick}
            commitLabel={commitLabel}
            committing={loading === "commit"}
            showTecDetailColumns={canViewTecLines}
            allowInvoiceAboveBudget={allowInvoiceAboveBudget}
          />
        </section>
      )}

      {step === "results" && commitResults && (
        <U3125ForfaitWizardCommitResults
          results={commitResults}
          showPdfAction={showPdfAction}
          onOpenPdf={(id) => void openPdfInNewTab(id)}
          onBackToList={handleBackToList}
          onNewSession={resetWizard}
        />
      )}

      <U2000SaveCancelDialog
        isOpen={confirmReleaseOpen}
        title={t("wizardReleaseConfirmTitle", { ns: NS })}
        onClose={(action) => {
          setConfirmReleaseOpen(false);
          if (action === "save") {
            void runCommit();
          }
        }}
        width="medium"
      >
        <p className="text-sm text-gray-700">{t("wizardReleaseConfirmMessage", { ns: NS })}</p>
      </U2000SaveCancelDialog>
    </div>
  );
};

export default U3125ForfaitWizardPage;
